/**
 * RequestDetails — File submit methods (edit, delete, visibility, patient details).
 * Mixed into RequestDetails.prototype.
 */
import { apiClient } from '../../utils/ApiClient.js';

export const FilesMixin = {

    async submitEditPatientDetailsForm(form) {
        if (form.dataset.submitting === '1') return;
        form.dataset.submitting = '1';
        const submitBtn = form.querySelector('button[type="submit"]');
        this.setBtnLoading(submitBtn, true);
        try {
            const json = await apiClient.post('docuswift_update_request_patient_details', {
                request_id:                      document.getElementById('edit_patient_request_id')?.value || '',
                request_patient_name:            document.getElementById('edit_patient_name')?.value || '',
                request_patient_date_of_birth:   document.getElementById('edit_patient_date_of_birth')?.value || '',
                request_patient_ssn:             document.getElementById('edit_patient_ssn')?.value || '',
                request_patient_address:         document.getElementById('edit_patient_address')?.value || '',
                request_treatment_from:          document.getElementById('edit_treatment_from')?.value || '',
                request_treatment_to:            document.getElementById('edit_treatment_to')?.value || '',
            });
            if (!json.success) {
                this.showToast(json.data?.message || 'Could not save patient details.', 'error');
                return;
            }
            this.closeEditPatientDetailsModal();
            this.showToast(json.data?.message || 'Patient details updated.');
            const rid = document.getElementById('edit_patient_request_id')?.value;
            if (rid && String(this.activeRequestId) === String(rid)) {
                this.fetchDetails(rid);
            }
        } catch (err) {
            console.error(err);
            this.showToast('An unexpected error occurred.', 'error');
        } finally {
            delete form.dataset.submitting;
            this.setBtnLoading(submitBtn, false);
        }
    },

    async submitEditCaseManagerForm(form) {
        if (form.dataset.submitting === '1') return;
        form.dataset.submitting = '1';
        const submitBtn = form.querySelector('button[type="submit"]');
        this.setBtnLoading(submitBtn, true);
        try {
            const json = await apiClient.post('docuswift_update_request_case_manager', {
                request_id: document.getElementById('edit_case_manager_request_id')?.value || '',
                case_manager_id: document.getElementById('edit_case_manager_select')?.value || '',
            });
            if (!json.success) {
                this.showToast(json.data?.message || 'Could not update case manager.', 'error');
                return;
            }
            this.closeEditCaseManagerModal();
            this.showToast(json.data?.message || 'Case manager updated.');
            const rid = document.getElementById('edit_case_manager_request_id')?.value;
            if (rid && String(this.activeRequestId) === String(rid)) {
                this.fetchDetails(rid);
            }
        } catch (err) {
            console.error(err);
            this.showToast('An unexpected error occurred.', 'error');
        } finally {
            delete form.dataset.submitting;
            this.setBtnLoading(submitBtn, false);
        }
    },

    async submitEditRoiSpecialistForm(form) {
        if (form.dataset.submitting === '1') return;
        form.dataset.submitting = '1';
        const submitBtn = form.querySelector('button[type="submit"]');
        this.setBtnLoading(submitBtn, true);
        try {
            const json = await apiClient.post('docuswift_update_request_roi_specialist', {
                request_id: document.getElementById('edit_roi_specialist_request_id')?.value || '',
                roi_specialist_id: document.getElementById('edit_roi_specialist_select')?.value || '',
            });
            if (!json.success) {
                this.showToast(json.data?.message || 'Could not update ROI specialist.', 'error');
                return;
            }
            this.closeEditRoiSpecialistModal();
            this.showToast(json.data?.message || 'ROI specialist updated.');
            const rid = document.getElementById('edit_roi_specialist_request_id')?.value;
            if (rid && String(this.activeRequestId) === String(rid)) {
                this.fetchDetails(rid);
            }
        } catch (err) {
            console.error(err);
            this.showToast('An unexpected error occurred.', 'error');
        } finally {
            delete form.dataset.submitting;
            this.setBtnLoading(submitBtn, false);
        }
    },

    async submitEditFile(form) {
        if (form.dataset.submitting === '1') return;
        form.dataset.submitting = '1';
        const submitBtn = form.querySelector('button[type="submit"]');
        this.setBtnLoading(submitBtn, true);
        try {
            const json = await apiClient.post('docuswift_update_request_file_meta', {
                request_id:    form.querySelector('[name="request_id"]')?.value    || '',
                attachment_id: form.querySelector('[name="attachment_id"]')?.value || '',
                file_category: form.querySelector('[name="file_category"]')?.value || '',
                file_status:   form.querySelector('[name="file_status"]')?.value   || '',
            });
            if (!json.success) {
                this.showToast(json.data?.message || 'Could not save changes.', 'error');
                return;
            }
            this.closeEditFileModal();
            this.showToast(json.data?.message || 'File updated successfully.');
            const rid = form.querySelector('[name="request_id"]')?.value;
            if (rid && String(this.activeRequestId) === String(rid)) {
                this.fetchDetails(rid);
            }
        } catch (err) {
            console.error(err);
            this.showToast('An unexpected error occurred.', 'error');
        } finally {
            delete form.dataset.submitting;
            this.setBtnLoading(submitBtn, false);
        }
    },

    async submitDeleteFile(rid, aid) {
        const confirmBtn = document.getElementById('delete_file_confirm');
        this.setBtnLoading(confirmBtn, true);
        try {
            const json = await apiClient.post('docuswift_delete_request_uploaded_file', {
                request_id:    rid,
                attachment_id: aid,
            });
            if (!json.success) {
                this.showToast(json.data?.message || 'Could not delete file.', 'error');
                return;
            }
            this.closeDeleteFileModal();
            this.showToast(json.data?.message || 'File deleted successfully.');
            if (String(this.activeRequestId) === String(rid)) {
                this.fetchDetails(rid);
            }
        } catch (err) {
            console.error(err);
            this.showToast('An unexpected error occurred.', 'error');
        } finally {
            this.setBtnLoading(confirmBtn, false);
        }
    },

    async submitRequestFileVisibilityForm(form) {
        if (form.dataset.submitting === '1') return;
        form.dataset.submitting = '1';
        const submitBtn = form.querySelector('button[type="submit"]');
        this.setBtnLoading(submitBtn, true);
        try {
            const data = {
                request_id:    form.querySelector('[name="request_id"]')?.value    || '',
                attachment_id: form.querySelector('[name="attachment_id"]')?.value || '',
            };
            ['vis_roi_specialist', 'vis_case_manager_portal', 'vis_request_owner'].forEach((name) => {
                const el = form.querySelector(`[name="${name}"]`);
                if (el?.checked) data[name] = '1';
            });
            const json = await apiClient.post('docuswift_update_request_file_visibility', data);
            if (!json.success) {
                this.showToast(json.data?.message || 'Could not save visibility.', 'error');
                return;
            }
            this.closeFileVisibilityModal();
            this.showToast(json.data?.message || 'Visibility saved successfully.');
            const rid = form.querySelector('[name="request_id"]')?.value;
            if (rid && String(this.activeRequestId) === String(rid)) {
                this.fetchDetails(rid);
            }
        } catch (err) {
            console.error(err);
            this.showToast('An unexpected error occurred.', 'error');
        } finally {
            delete form.dataset.submitting;
            this.setBtnLoading(submitBtn, false);
        }
    },
};
